<template>
  <div id="scrollable-automation" v-hotkey="shortcuts" class="automation">
    <!-- Sticky zero-height anchor: keeps the find bar pinned to the top of
         the screen while `#scrollable-automation` (this component's root)
         scrolls. -->
    <div v-if="isFindBarOpen" class="find-bar-anchor">
      <FindBar
        ref="findBarEl"
        container-id="scrollable-automation"
        @close="closeFindBar"
      />
    </div>
    <BaseTopBar v-if="showTopBar && isMounted" with-find @find="openFindBar">
      <template #left>
        <Breadcrumb :items="breadcrumbItems" :home="breadcrumbHome" />
      </template>
      <template #right>
        <BaseButton
          kind="main"
          type="primary"
          text="Save"
          :state="saveButtonState"
          @click="saveAutomation"
        />
        <BaseButton
          kind="main"
          type="secondary"
          text="Run"
          :state="runButtonState"
          @click="runAutomation"
        >
          <template #icon>
            <StartIcon />
          </template>
        </BaseButton>
        <ShareButton
          :root="automationShareRoot"
          :organization-id="organizationId"
        />
      </template>
      <template #more>
        <BaseButton
          class="context-action"
          kind="auxiliary"
          text="Copy Link"
          tint="none"
          is-expanded
          @click="copyAutomationLink"
        >
          <template #icon><CopyLinkIcon /></template>
          <template #right>{{ copyLinkShortcutDisplay }}</template>
        </BaseButton>
        <BaseButton
          class="context-action"
          kind="auxiliary"
          :text="isEnabled ? 'Disable' : 'Enable'"
          tint="none"
          is-expanded
          @click="toggleSuspend"
        >
          <template #icon
            ><PauseFillIcon v-if="isEnabled" /><StartIcon v-else
          /></template>
        </BaseButton>
        <BaseButton
          class="context-action"
          kind="auxiliary"
          text="Duplicate"
          tint="none"
          is-expanded
          @click="duplicateAutomation"
        >
          <template #icon><DuplicateIcon /></template>
        </BaseButton>
        <BaseButton
          class="context-action"
          kind="auxiliary"
          text="Delete"
          tint="red"
          is-expanded
          @click="deleteAutomation"
        >
          <template #icon><DeleteIcon /></template>
        </BaseButton>
      </template>
    </BaseTopBar>
    <div v-if="isLoading" class="automation-loading">
      <p>Loading automation...</p>
    </div>
    <template v-else>
      <div class="automation-content">
        <!-- Editable in place (like the Label screen title): typing saves
             after a debounce, and blurring — clicking outside — flushes the
             pending save immediately. -->
        <RInput
          ref="titleInput"
          v-model:value="editableName"
          class="automation-title"
          placeholder="Untitled"
          :debounce="5000"
          @enter="onTitleEnter"
          @blur="onTitleBlur"
        />

        <BaseTabs
          :tabs-titles="AUTOMATION_TABS"
          :selected-tab="activeTab"
          @update:selected-tab="activeTab = $event"
        />

        <RepeatModal ref="repeatModal" />

        <template v-if="activeTab === 'Configuration'">
          <!-- Trigger properties styled like Object.vue property rows -->
          <div class="trigger-section">
            <div class="prop-row">
              <div class="prop-label">
                <AutomationIcon />
                <span class="label-name">Trigger</span>
              </div>
              <div class="prop-value">
                <BaseInputSelect
                  :value="triggerType"
                  :options="triggerOptions"
                  select-only
                  @selected="onTriggerTypeChange($event.value)"
                />
              </div>
            </div>
            <BaseAlert
              v-if="triggerType === 'commit_pattern'"
              class="prop-alert"
              state="info"
              size="small"
            >
              {{ automationOnChangeDescription }}
            </BaseAlert>
            <template v-if="triggerType === 'recurrent'">
              <div class="prop-row">
                <div class="prop-label">
                  <RecurringIcon />
                  <span class="label-name">Frequency</span>
                </div>
                <div class="prop-value">
                  <ButtonMenuRecurrence
                    :recurrence="automationRecurrence"
                    :time="triggerTime"
                    @update:recurrence="onTriggerRecurrenceUpdate"
                    @custom="onTriggerCustomClick"
                  />
                </div>
              </div>
              <div class="prop-row">
                <div class="prop-label">
                  <TimeIcon />
                  <span class="label-name">Time</span>
                </div>
                <div class="prop-value">
                  <TimeSelectButton
                    :is-editable="true"
                    :is-edited="false"
                    :is24h-format="is24hFormat"
                    :current-time="
                      automationDaytime
                        ? {
                            hours: automationDaytime.hours,
                            minutes: automationDaytime.minutes ?? 0,
                          }
                        : undefined
                    "
                    @update:time="onTriggerDaytimeUpdate"
                  />
                </div>
              </div>
            </template>
            <div v-if="triggerType === 'commit_pattern'" class="prop-row">
              <div class="prop-label">
                <DatabaseIcon />
                <span class="label-name">Database</span>
              </div>
              <div class="prop-value">
                <TypeDatabasePicker
                  :selected-ids="automationSelectedDatabaseIds"
                  :org-id="organizationId"
                  multi-select
                  mode="database"
                  inline-placeholder="Select database"
                  @update:selected-ids="onAutomationDatabaseSelectionUpdate"
                />
              </div>
            </div>
            <div v-if="triggerType === 'commit_pattern'" class="prop-row">
              <div class="prop-label">
                <FilterIcon />
                <span class="label-name">On</span>
              </div>
              <div class="prop-value">
                <BaseInputSelect
                  :values="automationOnChange"
                  :options="eventKindOptions"
                  :multiple="true"
                  select-only
                  placeholder="Select events"
                  @update:values="onTriggerOnChangeUpdate"
                />
              </div>
            </div>
            <!-- Shortcut running the automation on the object page being
                 viewed (manual trigger only) — same as Mod+K → Run
                 automation, with the object passed as input. Device-local
                 and per-account; takes precedence over the app's other
                 shortcuts. -->
            <div v-if="triggerType === 'manual'" class="prop-row">
              <div class="prop-label">
                <ShortcutIcon />
                <span class="label-name">Command</span>
              </div>
              <div class="prop-value">
                <HotkeyCapture
                  class="prop-hotkey"
                  size="caption"
                  :value="draftHotkey"
                  @update:value="onHotkeyUpdate"
                />
              </div>
            </div>
            <div class="prop-row">
              <div class="prop-label">
                <AgentIcon />
                <span class="label-name">Agent</span>
              </div>
              <div class="prop-value prop-value--text">
                {{ automationAgentLabel }}
              </div>
            </div>
          </div>

          <BaseAlert v-if="!isEnabled" state="warning" size="small">
            This automation is disabled and won't run automatically or manually.
          </BaseAlert>

          <!-- Steps: always-expanded cards -->
          <div class="steps-section">
            <div v-if="steps.length === 0" class="steps-creating" />

            <template v-for="(step, index) in steps" :key="step.id">
              <div class="step-card">
                <div class="step-card-header">
                  <div class="step-card-header-info">
                    <span class="step-card-badge">{{ index + 1 }}</span>
                    <span
                      v-if="editingStepNameId !== step.id"
                      class="step-card-name"
                      @click="startEditingStepName(step.id)"
                      >{{ stepDraftNames[step.id] ?? step.name }}</span
                    >
                    <FormInput
                      v-else
                      class="step-card-name-input"
                      :value="stepDraftNames[step.id] ?? step.name"
                      @update:value="onStepNameChange(step.id, $event)"
                      @blur="stopEditingStepName(step.id)"
                      @keydown.enter.stop="blurEventTarget($event)"
                      @keydown.escape.stop="
                        stepDraftNames[step.id] = step.name;
                        blurEventTarget($event);
                      "
                    />
                  </div>
                  <BaseButton
                    v-if="index > 0"
                    kind="auxiliary"
                    type="tertiary"
                    tint="red"
                    text="Delete"
                    @click.stop="deleteStep(step.id)"
                  />
                </div>
                <div v-if="workspaceId" class="step-card-body">
                  <StepComponent
                    :ref="(el) => setStepRef(step.id, el)"
                    :step-id="step.id"
                    :organization-id="organizationId"
                    :workspace-id="workspaceId"
                    :integration-organization-ids="integrationOrganizationIds"
                    :org-id="referenceSearchOrganizationId"
                    :auto-save="false"
                    :placeholder="stepPlaceholder"
                    @dirty="hasUnsavedChanges = true"
                  />
                </div>
              </div>

              <div v-if="index < steps.length - 1" class="step-connector">
                <div class="step-connector-line" />
              </div>
            </template>

            <div class="step-add-row">
              <div class="step-connector-line" />
              <BaseButton
                kind="main"
                type="secondary"
                :text="
                  addStepButtonState === 'loading' ? 'Adding…' : 'Add step'
                "
                :state="
                  addStepButtonState === 'loading' ? 'loading' : 'default'
                "
                @click="addStep"
              >
                <template #icon>
                  <AddIcon />
                </template>
              </BaseButton>
            </div>
          </div>
        </template>

        <template v-else-if="activeTab === 'Runs'">
          <AutomationRunList
            :organization-id="organizationId"
            :automation-id="automationId"
            :active-run-id="selectedRunId ?? ''"
            @select="selectedRunId = $event"
          />

          <AutomationRunDetail
            v-if="selectedRunId"
            :run-id="selectedRunId"
            :organization-id="organizationId"
          />
          <div v-else class="runs-empty">No runs yet.</div>
        </template>
      </div>
    </template>
  </div>
</template>

<script lang="ts">
import { cloneDeep } from "lodash-es";
import {
  computed,
  defineComponent,
  h,
  nextTick,
  onMounted,
  reactive,
  ref,
  shallowRef,
  toRefs,
  watch,
  watchEffect,
  type ComponentPublicInstance,
} from "vue";
import { useModal } from "vue-final-modal";
import { onBeforeRouteLeave } from "vue-router";

import type {
  AgentId,
  AiAutomationSimplePattern,
  AiAutomationSimpleTrigger,
  AiAutomationTriggerEventKind,
  AiAutomationTriggerObjectKind,
  AiStep,
  AutomationId,
  Daytime,
  EventRecurrence,
  EventTime,
  OrgId,
  Recurrence,
  ReleasableRef,
  StepId,
  TableId,
  WorkspaceId,
} from "@ctrl/controller-rpc";

import { copy } from "@/clipboard";
import { automationActions } from "@/components/actions/actions";
import { isAssistant } from "@/components/ai/agents";
import AutomationRunDetail from "@/components/ai/AutomationRunDetail.vue";
import AutomationRunList from "@/components/ai/AutomationRunList.vue";
import { getAgentIntegrationOrganizationIds } from "@/components/ai/integrationOrganizations";
import StepComponent from "@/components/ai/Step.vue";
import { ORPHAN_AGENT_KEY } from "@/components/ai/useAutomations";
import BaseAlert from "@/components/base/BaseAlert.vue";
import BaseButton from "@/components/base/BaseButton.vue";
import BaseInputSelect, {
  type SelectOption,
} from "@/components/base/BaseInputSelect.vue";
import BaseTabs from "@/components/base/BaseTabs.vue";
import BaseTopBar from "@/components/base/BaseTopBar.vue";
import Breadcrumb, {
  type BreadcrumbHome,
  type BreadcrumbItem,
} from "@/components/Breadcrumb.vue";
import ButtonMenuRecurrence from "@/components/ButtonMenuRecurrence.vue";
import { isUserDefinedDatabase } from "@/components/databases";
import TimeSelectButton from "@/components/date-time/TimeSelectButton.vue";
import FindBar from "@/components/find/FindBar.vue";
import { useFindBar } from "@/components/find/useFindBar";
import FormInput from "@/components/graph/definition/form/FormInput.vue";
import { getTitleFieldName } from "@/components/graph/title";
import HotkeyCapture from "@/components/HotkeyCapture.vue";
import AddIcon from "@/components/icons/AddIcon.vue";
import AgentIcon from "@/components/icons/AgentIcon.vue";
import AutomationIcon from "@/components/icons/AutomationIcon.vue";
import CopyLinkIcon from "@/components/icons/CopyLinkIcon.vue";
import DatabaseIcon from "@/components/icons/DatabaseIcon.vue";
import DeleteIcon from "@/components/icons/DeleteIcon.vue";
import DuplicateIcon from "@/components/icons/DuplicateIcon.vue";
import FilterIcon from "@/components/icons/FilterIcon.vue";
import PauseFillIcon from "@/components/icons/PauseFillIcon.vue";
import RecurringIcon from "@/components/icons/RecurringIcon.vue";
import SearchIcon from "@/components/icons/SearchIcon.vue";
import ShortcutIcon from "@/components/icons/ShortcutIcon.vue";
import StartIcon from "@/components/icons/StartIcon.vue";
import TimeIcon from "@/components/icons/TimeIcon.vue";
import * as inject from "@/components/inject";
import type { ExportRoot } from "@/components/marketplace/export";
import { getRecurrenceWithStart, getStartDate } from "@/components/modal";
import Modal from "@/components/modals/Modal.vue";
import RepeatModal from "@/components/RepeatModal.vue";
import RInput from "@/components/RInput.vue";
import { UseSettings } from "@/components/settings/controller/settings";
import { injectSettingsTopbarActions } from "@/components/settings/topbarActions";
import ShareButton from "@/components/ShareButton.vue";
import {
  assignShortcuts,
  copyLinkItem,
  findInPage,
  Shortcuts,
} from "@/components/shortcuts";
import { defaultTableIds, definitiveTableId } from "@/components/tables";
import templates, { instantiate } from "@/components/templates";
import TypeDatabasePicker, {
  databasePickerId,
} from "@/components/TypeDatabasePicker.vue";
import { getOrganizationDefaultWorkspace } from "@/components/workspaces";
import { useWorkspaceDatabaseGroups } from "@/composables/useWorkspaceDatabaseGroups";
import {
  bindAsync,
  releasableRef,
  releaseBestEffort,
  useReleaser,
} from "@/ref";
import { RouteNames } from "@/routes/types";
import { hotkeys } from "@/utils/hotkeys";
import { keymap, type Keymap } from "@/utils/keymap";
import { isRecord } from "@/utils/unknown";

type StepRow = {
  id: StepId;
  name: string;
};

type TriggerKind = Exclude<
  AiAutomationTriggerObjectKind,
  { table_id: TableId; kind: "table_row" }
>["kind"];

const TRIGGER_KINDS = new Set<string>(["event", "task", "page", "people"]);

const isTriggerKind = (kind: string): kind is TriggerKind =>
  TRIGGER_KINDS.has(kind);

const triggerPatternId = (pattern: AiAutomationSimplePattern): string =>
  pattern.kind === "table_row"
    ? `${pattern.workspace_id}:table:${pattern.table_id}`
    : `${pattern.workspace_id}:kind:${pattern.kind}`;

export default defineComponent({
  name: "Automation",
  components: {
    AutomationRunDetail,
    AutomationRunList,
    BaseAlert,
    BaseButton,
    BaseInputSelect,
    BaseTabs,
    BaseTopBar,
    Breadcrumb,
    ButtonMenuRecurrence,
    AddIcon,
    AgentIcon,
    AutomationIcon,
    CopyLinkIcon,
    DatabaseIcon,
    DeleteIcon,
    DuplicateIcon,
    FilterIcon,
    FindBar,
    FormInput,
    HotkeyCapture,
    PauseFillIcon,
    RecurringIcon,
    RInput,
    ShareButton,
    ShortcutIcon,
    StartIcon,
    TimeIcon,
    RepeatModal,
    StepComponent,
    TimeSelectButton,
    TypeDatabasePicker,
  },
  props: {
    automationId: { type: String, required: true },
    organizationId: { type: String, required: true },
    showTopBar: { type: Boolean, default: true },
    initialRunId: { type: String, default: null },
  },
  async setup(props) {
    const {
      automationId: automationIdProp,
      organizationId: organizationIdProp,
    } = toRefs(props);
    const automationId = computed(
      () => automationIdProp.value satisfies AutomationId
    );
    const organizationId = computed(
      () => organizationIdProp.value satisfies OrgId
    );
    const controller = inject.controller();
    const logger = inject.logger("Automation");
    const profile = inject.profile();
    const automations = inject.automations();
    const automationHotkeys = inject.automationHotkeys();
    const agents = inject.agents();
    const router = inject.router();
    const isInputFocused = inject.isInputFocused();
    const isModalShown = inject.isModalShown();

    const releaser = useReleaser();

    const AUTOMATION_TABS = ["Configuration", "Runs"] as const;
    type AutomationTab = (typeof AUTOMATION_TABS)[number];
    const activeTab = ref<AutomationTab>("Configuration");

    const isMounted = ref(false);
    onMounted(() => {
      isMounted.value = true;
    });

    const { isFindBarOpen, findBarEl, openFindBar, closeFindBar } =
      useFindBar();

    const settingsTopbarActions = injectSettingsTopbarActions();
    const workspaceEntries = useWorkspaceDatabaseGroups();
    releaser.defer(() => {
      if (settingsTopbarActions && !props.showTopBar) {
        settingsTopbarActions.saveState = null;
        settingsTopbarActions.runState = null;
        settingsTopbarActions.onSave = null;
        settingsTopbarActions.onRun = null;
        settingsTopbarActions.shareRoot = null;
        settingsTopbarActions.shareOrganizationId = undefined;
        settingsTopbarActions.moreActions = [];
      }
    });

    // Form dirty flag — declared before the first await so that the
    // `onBeforeRouteLeave` guard can safely reference it.
    const hasUnsavedChanges = ref(false);

    // Route guard for unsaved changes. Must be registered synchronously
    // before any await: vue-router's `onBeforeRouteLeave` calls
    // `inject(matchedRouteKey, {}).value` without an instance check, so when
    // run after an await it throws "Cannot read properties of undefined
    // (reading 'value')" in production builds.
    onBeforeRouteLeave((_to, _from, next) => {
      if (!hasUnsavedChanges.value) {
        next();
        return;
      }
      const { open } = useModal({
        component: Modal,
        attrs: {
          title: "Unsaved Changes",
          icon: AutomationIcon,
          showCancelButton: true,
          modalButtons: [
            {
              title: "Discard",
              kind: "main",
              func: async () => {
                next();
              },
            },
          ],
        },
        slots: {
          default: () => h("p", "Your changes will be lost if you leave now."),
        },
      });
      open();
    });

    // Resolve workspaceId from organizationId
    const workspaceId = await releaser.add(
      bindAsync(organizationId, async (orgId) => {
        if (!orgId) return releasableRef<WorkspaceId | undefined>(undefined);
        const wsId = await getOrganizationDefaultWorkspace(controller, orgId);
        return releasableRef<WorkspaceId | undefined>(wsId);
      })
    );

    const breadcrumbHome = computed((): BreadcrumbHome | undefined => {
      const oid = organizationId.value;
      return oid ? { type: "organization", id: oid } : undefined;
    });

    // Subscribe to automation data reactively
    const automationDataRef = await controller.ai.automation.get(
      organizationId.value,
      automationId.value
    );

    const isLoading = computed(() => automationDataRef.value === undefined);

    // Display name (breadcrumb).
    const automationName = computed(() => {
      return automationDataRef.value?.name ?? "Untitled";
    });

    // ── Title (edited in place) ──────────────────────────────────────

    // Local copy so a pending debounced save doesn't get clobbered when the
    // reactive automation data refreshes (same pattern as GraphTitle).
    const localName = ref("");
    releaser.defer(
      watch(
        () => automationDataRef.value?.name,
        (name) => {
          localName.value = name ?? "";
        },
        { immediate: true }
      )
    );

    const editableName = computed({
      get: () => localName.value,
      set: (value: string | undefined) => {
        localName.value = value ?? "";
        const name = (value ?? "").trim();
        // Never persist an empty name; onTitleBlur restores the saved one.
        if (!name || name === automationDataRef.value?.name) return;
        controller.ai.automation
          .update(organizationId.value, automationId.value, { name })
          .catch((e: unknown) => {
            logger.error("rename: error", { error: e });
          });
      },
    });

    const titleInput = ref<InstanceType<typeof RInput>>();
    // Enter commits: blurring flushes the debounced save and drops the caret.
    const onTitleEnter = () => titleInput.value?.blur();
    // Blurring an emptied title restores the saved name (it is never saved).
    const onTitleBlur = () => {
      if (!localName.value.trim()) {
        localName.value = automationDataRef.value?.name ?? "";
      }
    };

    // ── Runs tab ─────────────────────────────────────────────────────

    const selectedRunId = ref<string | null>(null);

    if (props.initialRunId) {
      activeTab.value = "Runs";
      selectedRunId.value = props.initialRunId;
    }

    // Find the parent agent for this automation by scanning the automations
    // index, which groups automations by organization and agent.
    const parentAgentId = computed<AgentId | undefined>(() => {
      const byOrg = automations.value?.all.value?.[organizationId.value];
      if (!byOrg) return undefined;
      for (const [aId, byAutomation] of Object.entries(byOrg)) {
        if (aId === ORPHAN_AGENT_KEY) continue;
        if (automationId.value in byAutomation) return aId;
      }
      return undefined;
    });

    const parentAgentName = computed<string>(() => {
      const aId = parentAgentId.value;
      if (!aId) return "Agent";
      if (isAssistant(aId)) return "Assistant";
      const list = agents.value?.active?.[organizationId.value] ?? [];
      return list.find((a) => a.id === aId)?.name ?? "Agent";
    });
    const automationAgentLabel = computed(() =>
      parentAgentId.value ? parentAgentName.value : "None"
    );

    // Assistant automations may use integrations from every organization;
    // other automations stay scoped to their organization (the editor's
    // workspace-scoped default).
    const integrationOrganizationIds = computed<OrgId[] | undefined>(() => {
      if (!profile.value) return undefined;
      const agentId = parentAgentId.value;
      return getAgentIntegrationOrganizationIds(
        profile.value,
        agentId !== undefined && isAssistant(agentId)
      );
    });

    // Likewise for the step editors' `[[` / `@` reference searches: the
    // assistant acts on the user's behalf everywhere, so its automations may
    // reference objects from all organizations (`null`); other automations
    // keep the editor's workspace-scoped default (`undefined`).
    const referenceSearchOrganizationId = computed<OrgId | null | undefined>(
      () => {
        const agentId = parentAgentId.value;
        return agentId !== undefined && isAssistant(agentId) ? null : undefined;
      }
    );

    const breadcrumbItems = computed<BreadcrumbItem[]>(() => {
      const items: BreadcrumbItem[] = [
        {
          title: "Agents",
          link: {
            name: RouteNames.SettingsAgents,
            params: { orgId: organizationId.value },
          },
        },
      ];
      if (parentAgentId.value) {
        items.push({
          title: parentAgentName.value,
          link: {
            name: RouteNames.Agent,
            params: { agentId: parentAgentId.value },
          },
        });
      }
      items.push({ title: automationName.value, link: { params: {} } });
      return items;
    });

    // Subscribe to the reactive step ID list from the controller.
    const stepListRef = await releaser.add(
      controller.ai.automation.step.all(
        organizationId.value,
        automationId.value
      )
    );

    // Fetch step data for each step ID reactively.
    const stepDataRefs = shallowRef<Map<StepId, ReleasableRef<AiStep | null>>>(
      new Map()
    );
    const updateStepDataRefs = async (stepIds: StepId[]) => {
      const prev = stepDataRefs.value;
      const next = new Map<StepId, ReleasableRef<AiStep | null>>();
      for (const stepId of stepIds) {
        if (prev.has(stepId)) {
          next.set(stepId, prev.get(stepId)!);
        } else {
          const dataRef = await controller.ai.step.get(
            organizationId.value,
            stepId
          );
          next.set(stepId, dataRef);
        }
      }
      // Release refs no longer needed.
      for (const [id, ref] of prev) {
        if (!next.has(id)) releaseBestEffort(ref);
      }
      stepDataRefs.value = next;
    };
    await updateStepDataRefs(stepListRef.value ?? []);
    releaser.defer(
      watch(stepListRef, (ids) => {
        void updateStepDataRefs(ids ?? []);
      })
    );

    const steps = computed<StepRow[]>(() => {
      const stepIds: StepId[] = stepListRef.value ?? [];
      return stepIds.map((stepId) => {
        const dataRef = stepDataRefs.value.get(stepId);
        return {
          id: stepId,
          name: dataRef?.value?.name ?? "Untitled",
        };
      });
    });

    const expandedStepId = ref<StepId | null>(null);
    const toggleStep = (stepId: StepId) => {
      expandedStepId.value = expandedStepId.value === stepId ? null : stepId;
    };

    const editingStepNameId = ref<StepId | null>(null);
    const blurEventTarget = (event: Event) => {
      if (event.target instanceof HTMLInputElement) event.target.blur();
    };
    const startEditingStepName = async (stepId: StepId) => {
      editingStepNameId.value = stepId;
      await nextTick();
      const input = document.activeElement;
      if (input && !(input instanceof HTMLInputElement)) {
        const found = document.querySelector<HTMLInputElement>(
          `.step-name-input input`
        );
        found?.focus();
      }
    };

    const deleteStep = async (stepId: StepId) => {
      if (expandedStepId.value === stepId) {
        expandedStepId.value = null;
      }
      await controller.ai.step.delete(organizationId.value, stepId);
    };

    // ── Form state ──────────────────────────────────────────────────
    // The automation page is a form: all changes stay local until Save.
    // `hasUnsavedChanges` is declared at the top of setup so the route guard
    // (which must be registered before any await) can reference it.

    // Step names
    const stepDraftNames = reactive<Record<StepId, string>>({});
    const onStepNameChange = (stepId: StepId, value: string) => {
      stepDraftNames[stepId] = value;
      hasUnsavedChanges.value = true;
    };

    const stopEditingStepName = (stepId: StepId) => {
      editingStepNameId.value = null;
      // Revert empty drafts to the server value; actual persistence happens on Save.
      const draft = stepDraftNames[stepId];
      if (draft !== undefined && draft.trim() === "") {
        delete stepDraftNames[stepId];
      }
    };

    // Step component refs (for calling save() on each)
    type StepComponentRef = { save?: () => void };
    const stepComponentRefs: Record<string, StepComponentRef> = {};
    const setStepRef = (
      stepId: StepId,
      el: Element | ComponentPublicInstance | null
    ) => {
      if (el && "save" in el && typeof el.save === "function") {
        const save = el.save;
        stepComponentRefs[stepId] = {
          save: () => Reflect.apply(save, el, []),
        };
      } else {
        delete stepComponentRefs[stepId];
      }
    };

    // Trigger draft — initialized from server, mutated locally
    const draftTrigger = ref<AiAutomationSimpleTrigger | undefined>(undefined);
    releaser.defer(
      watch(
        () => automationDataRef.value?.trigger,
        (serverTrigger) => {
          if (!hasUnsavedChanges.value && serverTrigger) {
            draftTrigger.value = cloneDeep(serverTrigger);
          }
        },
        { immediate: true, deep: true }
      )
    );

    const markDirty = () => {
      hasUnsavedChanges.value = true;
    };

    // Shortcut draft — the in-app hotkey that runs this automation on the
    // object page being viewed (manual trigger only). Device-local and
    // per-account (browser store), so it is not part of the automation
    // record; still saved through the same Save flow as the rest of the
    // form.
    const draftHotkey = ref<Keymap.Key[] | null>(
      automationHotkeys.value?.get(automationId.value) ?? null
    );
    const onHotkeyUpdate = (keys: Keymap.Key[] | null) => {
      draftHotkey.value = keys;
      markDirty();
    };

    // ── Run ─────────────────────────────────────────────────────────

    // A block's text content (if any). Returns "" for blocks without a text
    // content field (e.g. divider, object).
    const blockText = (block: unknown): string => {
      if (!isRecord(block)) return "";
      return typeof block.content === "string" ? block.content : "";
    };

    const stepHasText = (stepId: StepId): boolean => {
      const data = stepDataRefs.value.get(stepId)?.value;
      const blocks = data?.action?.prompt?.document?.blocks;
      if (!blocks) return false;
      return blocks.some((b) => blockText(b).trim() !== "");
    };

    const allStepsHaveText = computed(() => {
      const stepList = steps.value;
      if (stepList.length === 0) return false;
      return stepList.every((s) => stepHasText(s.id));
    });

    const isEnabled = computed(() => automationDataRef.value?.enabled ?? true);

    const isRunning = ref(false);
    const runButtonState = computed(() => {
      if (!isEnabled.value) return "disabled";
      if (isRunning.value || isSaving.value) return "loading";
      if (!allStepsHaveText.value) return "disabled";
      return "default";
    });

    const runAutomation = async () => {
      if (isRunning.value) return;
      if (hasUnsavedChanges.value) {
        await saveAutomation();
      }
      if (!allStepsHaveText.value) {
        logger.error(
          "runAutomation",
          "Cannot run automation: all steps must have a non-empty prompt"
        );
        return;
      }
      const allValue = automations.value?.all.value;
      if (!allValue) return;
      let orgId: OrgId | undefined;
      let agentId: AgentId | null | undefined;
      for (const [oId, byAgent] of Object.entries(allValue)) {
        for (const [aId, byAutomation] of Object.entries(byAgent)) {
          if (automationId.value in byAutomation) {
            orgId = oId;
            agentId = aId === ORPHAN_AGENT_KEY ? null : aId;
            break;
          }
        }
        if (orgId) break;
      }
      if (!orgId || agentId === undefined) {
        logger.error("runAutomation", "Could not find automation in index");
        return;
      }
      isRunning.value = true;
      try {
        await automations.value!.run(
          orgId,
          agentId,
          automationId.value,
          undefined
        );
      } finally {
        isRunning.value = false;
      }
    };

    // ── Add step ────────────────────────────────────────────────────

    const isAddingStep = ref(false);
    const addStepButtonState = computed(() => {
      if (isAddingStep.value) return "loading";
      return "default";
    });

    const addStep = async () => {
      if (isAddingStep.value) return;
      if (automationDataRef.value === undefined) return;

      isAddingStep.value = true;
      try {
        const stepNumber = (stepListRef.value?.length ?? 0) + 1;
        const newStepId = await controller.ai.step.create(
          organizationId.value,
          {
            name: `Step ${stepNumber}`,
            prompt: {
              context: {},
              document: { blocks: instantiate(templates.empty) },
            },
          },
          automationId.value
        );
        expandedStepId.value = newStepId;
      } catch (e) {
        logger.error("addStep: error", { error: e });
      } finally {
        isAddingStep.value = false;
      }
    };

    // Auto-create the first step so the editor is always immediately visible.
    // Only fires once on load; if the user later deletes all steps they can
    // use the "+ Add another step" link to recover. The flag is locked the
    // first time we observe the loaded state so that later empty-step states
    // (e.g. the automation being deleted from elsewhere, which makes
    // `stepListRef` return `[]`) don't re-trigger auto-creation on a
    // nonexistent automation.
    const hasSeenLoadedState = ref(false);
    releaser.defer(
      watch(
        [isLoading, steps],
        ([loading, currentSteps]) => {
          if (loading || hasSeenLoadedState.value) return;
          if (automationDataRef.value === undefined) return;
          hasSeenLoadedState.value = true;
          if (currentSteps.length === 0) {
            void addStep();
          }
        },
        { immediate: true }
      )
    );

    // ── Trigger controls ────────────────────────────────────────────

    const { is24hFormat } = UseSettings.getInstance();
    const triggerTime = computed<EventTime>(() => ({ date: new Date() }));
    const repeatModal = ref<InstanceType<typeof RepeatModal>>();
    const currentTimezone = await controller.timezone();

    const triggerRecurrenceTarget = ref<{
      recurrence?: Recurrence;
      start: Date;
      time: EventTime;
    }>({
      recurrence: undefined,
      start: new Date(),
      time: triggerTime.value,
    });

    const ALL_EVENT_KINDS: AiAutomationTriggerEventKind[] = [
      "creation",
      "update",
      "deletion",
    ];

    const eventKindOptions: SelectOption[] = [
      { value: "creation", label: "Creation", aliases: [] },
      { value: "update", label: "Update", aliases: [] },
      { value: "deletion", label: "Deletion", aliases: [] },
    ];

    const triggerOptions: SelectOption[] = [
      { value: "manual", label: "Manual", aliases: [] },
      { value: "recurrent", label: "Scheduled", aliases: [] },
      { value: "commit_pattern", label: "Reaction", aliases: [] },
    ];

    const triggerType = computed<"manual" | "recurrent" | "commit_pattern">(
      () => {
        const trigger = draftTrigger.value;
        if (trigger?.trigger === "recurrent") return "recurrent";
        if (trigger?.trigger === "commit_pattern") return "commit_pattern";
        return "manual";
      }
    );

    const buildRecurrentTrigger = (
      recurrence: Recurrence,
      daytime?: Daytime
    ): AiAutomationSimpleTrigger => {
      const effectiveDaytime: Daytime =
        daytime ??
        (draftTrigger.value?.trigger === "recurrent"
          ? draftTrigger.value.daytime
          : { hours: 9, minutes: 0 });
      const eventRecurrence: EventRecurrence = getRecurrenceWithStart(
        recurrence,
        triggerTime.value
      );
      return {
        trigger: "recurrent",
        recurrence: eventRecurrence,
        daytime: effectiveDaytime,
        timezone: currentTimezone,
      };
    };

    const onTriggerTypeChange = (
      type: "manual" | "recurrent" | "commit_pattern" | string
    ) => {
      if (type === triggerType.value) return;
      if (type === "manual") {
        draftTrigger.value = { trigger: "manual" };
      } else if (type === "commit_pattern") {
        draftTrigger.value = {
          trigger: "commit_pattern",
          watched: [],
          on_change: ALL_EVENT_KINDS,
        };
      } else {
        draftTrigger.value = buildRecurrentTrigger({ rule: { every: "day" } });
      }
      markDirty();
    };

    const automationRecurrence = computed<Recurrence | undefined>(() => {
      const trigger = draftTrigger.value;
      if (!trigger || trigger.trigger !== "recurrent") return undefined;
      return trigger.recurrence;
    });

    const automationDaytime = computed<Daytime | undefined>(() => {
      const trigger = draftTrigger.value;
      if (!trigger || trigger.trigger !== "recurrent") return undefined;
      return trigger.daytime;
    });

    const onTriggerRecurrenceUpdate = (recurrence: Recurrence | undefined) => {
      if (!recurrence) {
        draftTrigger.value = { trigger: "manual" };
      } else {
        draftTrigger.value = buildRecurrentTrigger(recurrence);
      }
      markDirty();
    };

    releaser.defer(
      watch(automationRecurrence, (r) => {
        triggerRecurrenceTarget.value.recurrence = r;
      })
    );

    releaser.defer(
      watch(
        () => triggerRecurrenceTarget.value.recurrence,
        (r) => {
          if (r !== automationRecurrence.value) {
            onTriggerRecurrenceUpdate(r);
          }
        }
      )
    );

    const onTriggerDaytimeUpdate = (daytime: Daytime) => {
      const trigger = draftTrigger.value;
      if (!trigger || trigger.trigger !== "recurrent") return;
      draftTrigger.value = { ...trigger, daytime };
      markDirty();
    };

    const automationWatched = computed<AiAutomationSimplePattern[]>(() => {
      const trigger = draftTrigger.value;
      if (!trigger || trigger.trigger !== "commit_pattern") return [];
      return trigger.watched;
    });

    const automationDatabasePatterns = computed<
      Map<string, AiAutomationSimplePattern>
    >(() => {
      const patterns = new Map<string, AiAutomationSimplePattern>();
      for (const workspace of workspaceEntries.value) {
        if (workspace.orgId !== organizationId.value) continue;
        for (const database of Object.values(workspace.databases)) {
          const defaultKind = defaultTableIds.get(
            definitiveTableId(database.id)
          );
          if (defaultKind && isTriggerKind(defaultKind)) {
            patterns.set(databasePickerId(database), {
              workspace_id: workspace.id,
              kind: defaultKind,
            });
          } else if (
            isUserDefinedDatabase(database) ||
            database.origin === "integration_app"
          ) {
            patterns.set(databasePickerId(database), {
              workspace_id: workspace.id,
              table_id: database.id,
              kind: "table_row",
            });
          }
        }
      }
      return patterns;
    });

    const automationSelectedDatabaseIds = computed<string[]>(() => {
      const watched = new Set(
        automationWatched.value.map((pattern) => triggerPatternId(pattern))
      );
      return [...automationDatabasePatterns.value]
        .filter(([, pattern]) => watched.has(triggerPatternId(pattern)))
        .map(([id]) => id);
    });

    const automationOnChange = computed<AiAutomationTriggerEventKind[]>(() => {
      const trigger = draftTrigger.value;
      if (!trigger || trigger.trigger !== "commit_pattern")
        return ALL_EVENT_KINDS;
      return trigger.on_change;
    });

    const automationOnChangeDescription = computed<string>(() => {
      const kinds = automationOnChange.value;
      if (kinds.length === 0) {
        return "This automation will not run because no database change events are selected.";
      }

      const labels = kinds.map((kind) => {
        switch (kind) {
          case "creation":
            return "created";
          case "update":
            return "updated";
          case "deletion":
            return "deleted";
        }
      });
      const changeLabel =
        labels.length === 1
          ? labels[0]
          : labels.slice(0, -1).join(", ") + " or " + labels[labels.length - 1];
      return `This automation will run whenever an object is ${changeLabel} in the selected database.`;
    });

    // Reaction triggers get an example placeholder tailored to the watched
    // database; otherwise `undefined` keeps the step's default placeholder.
    const stepPlaceholder = computed<string | undefined>(() => {
      const trigger = draftTrigger.value;
      if (!trigger || trigger.trigger !== "commit_pattern") {
        return undefined;
      }

      const watched = trigger.watched;
      const kinds = trigger.on_change;
      const hasCreation = kinds.includes("creation");
      const hasUpdate = kinds.includes("update");
      const hasDeletion = kinds.includes("deletion");

      // Resolve kind label and title field name from the first watched pattern
      const first = watched[0];
      let kindLabel = "object";
      let titleField = "title";
      if (first) {
        if (first.kind === "table_row") {
          const ws = workspaceEntries.value.find(
            (e) => e.id === first.workspace_id
          );
          const db = ws?.databases[first.table_id];
          kindLabel = db?.data_name ?? "record";
          titleField = (db ? getTitleFieldName(db) : undefined) ?? "title";
        } else {
          const kindMap: Record<string, string> = {
            task: "task",
            event: "event",
            page: "page",
            people: "person",
            task_recurrent: "recurring task",
            event_recurrent: "recurring event",
          };
          kindLabel = kindMap[first.kind] ?? first.kind;
        }
      }

      if (kinds.length === 0) return undefined;

      const sentences: string[] = [];
      if (hasUpdate) {
        sentences.push(
          `When a ${kindLabel}'s ${titleField} changes from A to B, do…`
        );
      }
      if (hasCreation) {
        // Simplified when combined with update (A/B already conveys the field)
        const creationText = hasUpdate
          ? `When a ${kindLabel} is created, do…`
          : `When a ${kindLabel} is created with ${titleField} A, do…`;
        sentences.push(creationText);
      }
      // Omit deletion when all three are selected — placeholder is long enough
      if (hasDeletion && !(hasUpdate && hasCreation)) {
        sentences.push(`When a ${kindLabel} is deleted, do…`);
      }

      return sentences.join(" ");
    });

    const onTriggerWatchedUpdate = (watched: AiAutomationSimplePattern[]) => {
      const trigger = draftTrigger.value;
      const on_change =
        trigger?.trigger === "commit_pattern"
          ? trigger.on_change
          : ALL_EVENT_KINDS;
      draftTrigger.value = { trigger: "commit_pattern", watched, on_change };
      markDirty();
    };

    const onAutomationDatabaseSelectionUpdate = (ids: string[]) => {
      const watched = ids.flatMap((id) => {
        const pattern = automationDatabasePatterns.value.get(id);
        return pattern ? [pattern] : [];
      });
      onTriggerWatchedUpdate(watched);
    };

    const onTriggerOnChangeUpdate = (on_change: string[]) => {
      const trigger = draftTrigger.value;
      const watched =
        trigger?.trigger === "commit_pattern" ? trigger.watched : [];
      const validOnChange = on_change.flatMap((value) => {
        const eventKind = ALL_EVENT_KINDS.find(
          (candidate) => candidate === value
        );
        return eventKind ? [eventKind] : [];
      });
      draftTrigger.value = {
        trigger: "commit_pattern",
        watched,
        on_change: validOnChange,
      };
      markDirty();
    };

    const onTriggerCustomClick = () => {
      triggerRecurrenceTarget.value = {
        recurrence: automationRecurrence.value,
        start: getStartDate(triggerTime.value) ?? new Date(),
        time: triggerTime.value,
      };
      repeatModal.value?.showModal(triggerRecurrenceTarget);
    };

    // ── Save ────────────────────────────────────────────────────────

    const isSaving = ref(false);

    const saveButtonState = computed(() => {
      if (isSaving.value) return "loading";
      if (!hasUnsavedChanges.value) return "disabled";
      return "default";
    });

    const saveAutomation = async () => {
      if (!hasUnsavedChanges.value || isSaving.value) return;
      isSaving.value = true;
      try {
        const promises: Promise<unknown>[] = [];

        // Trigger
        if (draftTrigger.value) {
          promises.push(
            controller.ai.automation.update(
              organizationId.value,
              automationId.value,
              { trigger: draftTrigger.value }
            )
          );
        }

        // Step names
        for (const step of steps.value) {
          const draft = stepDraftNames[step.id];
          if (draft !== undefined && draft.trim() !== step.name.trim()) {
            const trimmed = draft.trim();
            if (trimmed) {
              promises.push(
                controller.ai.step.update(organizationId.value, step.id, {
                  name: trimmed,
                })
              );
            }
          }
        }

        // Step prompts
        for (const stepRef of Object.values(stepComponentRefs)) {
          if (stepRef?.save) stepRef.save();
        }

        // Shortcut: stored locally per account (not in the automation
        // record). Cleared when the trigger is no longer manual.
        automationHotkeys.value?.set(
          automationId.value,
          triggerType.value === "manual" ? draftHotkey.value : null
        );

        await Promise.all(promises);
        hasUnsavedChanges.value = false;
      } catch (e) {
        logger.error("saveAutomation: error", { error: e });
      } finally {
        isSaving.value = false;
      }
    };

    // ── More menu actions ────────────────────────────────────────────

    const copyLinkShortcutDisplay =
      hotkeys.format.stringify(
        [keymap.keyMod, keymap.keyShift, keymap.keyL],
        keymap.formatSymbol,
        keymap.separatorSpace
      ) ?? "";

    const findShortcutDisplay =
      hotkeys.format.stringify(
        [keymap.keyMod, keymap.keyF],
        keymap.formatSymbol,
        keymap.separatorSpace
      ) ?? "";

    const copyAutomationLink = async () => {
      const url = `https://open.routine.co?path=/automations/${automationId.value}?organizationId=${organizationId.value}`;
      await copy(url);
      controller.notifications.notify("Link copied to clipboard");
    };

    const automationShareRoot = computed<ExportRoot>(() => ({
      kind: "automation",
      automationId: automationId.value,
      organizationId: organizationId.value,
    }));

    const shortcuts = computed<Shortcuts>(() => ({
      ...assignShortcuts(
        { isModalShown, isInputFocused },
        copyLinkItem,
        () => void copyAutomationLink()
      ),
      // With an own top bar, Mod+F is bound by BaseTopBar (`with-find`); in
      // the Settings-embedded editor (no own top bar) bind it here. Like in
      // a browser, find stays available while an input (e.g. a step's
      // prompt editor) is focused.
      ...(props.showTopBar
        ? {}
        : assignShortcuts(
            { isModalShown, isInputFocused: ref(false) },
            findInPage,
            () => {
              openFindBar();
            }
          )),
    }));

    const deleteAutomation = async () => {
      await controller.ai.automation.delete(
        organizationId.value,
        automationId.value
      );
      // Drop the device-local shortcut bound to the deleted automation.
      automationHotkeys.value?.set(automationId.value, null);
      const agentId = parentAgentId.value;
      if (agentId) {
        router.push({
          name: RouteNames.Agent,
          params: { agentId },
        });
      } else {
        router.push({
          name: RouteNames.SettingsAgents,
          params: { orgId: organizationId.value },
        });
      }
    };

    const toggleSuspend = async () => {
      try {
        await controller.ai.automation.update(
          organizationId.value,
          automationId.value,
          { enabled: !isEnabled.value }
        );
      } catch (e) {
        logger.error("toggleSuspend: error", { error: e });
      }
    };

    // Same automation under the same agent, but disabled and named
    // "... (copy)" — the shared `automationActions.duplicate`, also offered
    // by the automation lists' menus. The action then navigates to the copy.
    const duplicateAutomation = () => {
      automationActions.duplicate.action(
        {
          type: "automation",
          value: {
            organizationId: organizationId.value,
            id: automationId.value,
            name: automationDataRef.value?.name ?? "",
            enabled: isEnabled.value,
          },
        },
        { controller, automations, router }
      );
    };

    // ────────────────────────────────────────────────────────────────

    if (settingsTopbarActions && !props.showTopBar) {
      releaser.defer(
        watchEffect(() => {
          settingsTopbarActions.saveState = saveButtonState.value;
          settingsTopbarActions.runState = runButtonState.value;
          settingsTopbarActions.onSave = saveAutomation;
          settingsTopbarActions.onRun = runAutomation;
          settingsTopbarActions.shareRoot = automationShareRoot.value;
          settingsTopbarActions.shareOrganizationId = organizationId.value;
          settingsTopbarActions.moreActions = [
            {
              text: "Find",
              icon: SearchIcon,
              shortcutsDisplay: findShortcutDisplay,
              onClick: openFindBar,
            },
            {
              text: "Copy Link",
              icon: CopyLinkIcon,
              shortcutsDisplay: copyLinkShortcutDisplay,
              onClick: copyAutomationLink,
            },
            {
              text: isEnabled.value ? "Disable" : "Enable",
              icon: isEnabled.value ? PauseFillIcon : StartIcon,
              onClick: toggleSuspend,
            },
            {
              text: "Duplicate",
              icon: DuplicateIcon,
              onClick: duplicateAutomation,
            },
            {
              text: "Delete",
              icon: DeleteIcon,
              tint: "red",
              onClick: deleteAutomation,
            },
          ];
        })
      );
    }

    return {
      AUTOMATION_TABS,
      activeTab,
      closeFindBar,
      findBarEl,
      isFindBarOpen,
      openFindBar,
      isMounted,
      isLoading,
      automationName,
      editableName,
      titleInput,
      onTitleEnter,
      onTitleBlur,
      selectedRunId,
      automationAgentLabel,
      breadcrumbItems,
      steps,
      expandedStepId,
      toggleStep,
      editingStepNameId,
      blurEventTarget,
      startEditingStepName,
      stopEditingStepName,
      deleteStep,
      stepDraftNames,
      onStepNameChange,
      setStepRef,
      hasUnsavedChanges,
      addStep,
      addStepButtonState,
      isEnabled,
      runAutomation,
      runButtonState,
      saveAutomation,
      saveButtonState,
      toggleSuspend,
      workspaceId,
      is24hFormat,
      triggerOptions,
      eventKindOptions,
      triggerTime,
      triggerType,
      onTriggerTypeChange,
      draftHotkey,
      onHotkeyUpdate,
      automationRecurrence,
      automationDaytime,
      onTriggerRecurrenceUpdate,
      onTriggerDaytimeUpdate,
      onTriggerCustomClick,
      repeatModal,
      triggerRecurrenceTarget,
      automationSelectedDatabaseIds,
      automationOnChange,
      automationOnChangeDescription,
      stepPlaceholder,
      onAutomationDatabaseSelectionUpdate,
      onTriggerOnChangeUpdate,
      copyAutomationLink,
      copyLinkShortcutDisplay,
      automationShareRoot,
      deleteAutomation,
      duplicateAutomation,
      breadcrumbHome,
      shortcuts,
      parentAgentId,
      integrationOrganizationIds,
      referenceSearchOrganizationId,
    };
  },
});
</script>

<style scoped lang="scss">
@use "@/styles/context-actions.scss";

.automation {
  width: 100%;
  overflow-y: auto;

  .find-bar-anchor {
    position: sticky;
    top: 0;
    height: 0;
    z-index: $z-index-dropdown;
  }

  .automation-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 400px;
    width: 100%;

    p {
      @extend %font-body-regular;
      @include themed() {
        color: t($grey-60);
      }
    }
  }

  .automation-content {
    display: flex;
    flex-direction: column;
    row-gap: 24px;
    width: 100%;
    max-width: $document-max-width;
    margin: 48px auto 48px;
    padding: 0;
    box-sizing: border-box;
  }

  .runs-empty {
    @extend %font-body-regular;

    @include themed() {
      color: t($grey-40);
    }
  }

  // Editable title (RInput): style the contenteditable and its placeholder
  // like the h1 it replaces (see .label-title in Label.vue).
  .automation-title {
    margin: 0;
    max-width: fit-content;
    // Keep a comfortable click target when the name is empty (the
    // placeholder is absolutely positioned and adds no width).
    min-width: 200px;

    @include themed() {
      color: t($primary);
    }

    :deep(.input),
    :deep(.placeholder) {
      @extend %font-h1;
    }
  }

  .tabs {
    margin-top: 12px;
  }

  // Trigger properties — Object.vue-style property rows
  .trigger-section {
    display: flex;
    flex-direction: column;
  }

  .prop-row {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    width: 100%;

    .prop-label {
      display: flex;
      flex-direction: row;
      align-items: center;
      column-gap: 14px;
      width: 35%;
      min-width: 0;
      height: 36px;

      svg {
        height: 14px;
        width: 14px;
        flex-shrink: 0;
      }

      :deep(.fill) {
        @include themed() {
          fill: t($grey-50);
        }
      }

      :deep(.stroke) {
        @include themed() {
          stroke: t($grey-50);
        }
      }

      .label-name {
        @extend %font-body-medium;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;

        @include themed() {
          color: t($primary);
        }
      }
    }

    .prop-value {
      display: flex;
      align-items: center;
      width: 65%;
      min-height: 36px;
      padding: 0 4px;
      border-radius: $border-radius-m;
      @include transition(background 0.2s ease-out);

      &--text {
        @extend %font-caption-regular;
        padding: 0 32px;

        @include themed() {
          color: t($grey-40);
        }

        &:hover {
          background: none;
        }
      }

      // Compact hotkey field (Command row): a small caption-sized box like
      // the neighboring select controls, not the default full-width input.
      // Padding matches BaseInputSelect's md control (4px around the 19px
      // caption line) so the box height lines up with the Trigger select.
      .prop-hotkey :deep(input) {
        width: 96px;
        padding: 4px 8px;
      }
    }
  }

  .prop-alert {
    margin-inline-start: 35%;
    width: 65%;

    :deep(.alert) {
      margin-bottom: 0;
    }
  }

  // Steps section
  .steps-section {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    row-gap: 0;
    margin-top: 24px;
  }

  // Transient placeholder while the first step is being auto-created
  .steps-creating {
    height: 100px;
    border-radius: 8px;
    @include themed() {
      background-color: t($bg-alt);
    }
  }

  // Step card
  .step-card {
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    width: 100%;
    overflow: hidden;
    @include themed() {
      border: 1px solid t($grey-15);
    }
  }

  .step-card-header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding-block: 10px;
    padding-inline-end: 8px;
    padding-inline-start: 12px;
    @include themed() {
      border-bottom: 1px solid t($grey-10);
    }
  }

  .step-card-header-info {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;
    min-width: 0;
  }

  .step-card-badge {
    flex-shrink: 0;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    @extend %font-body-regular;
    @include themed() {
      background-color: t($tertiary-10);
      color: t($tertiary-50);
    }
  }

  .step-card-name {
    min-width: 0;
    cursor: text;
    @extend %font-body-regular;
    @include themed() {
      color: t($grey-60);
    }

    &:hover {
      @include themed() {
        color: t($primary);
      }
    }
  }

  .step-card-name-input {
    flex: 1;
    min-width: 0;
  }

  .step-card-body {
    padding: 12px;
  }

  // Arrow connector between step cards
  .step-connector {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .step-connector-line {
    width: 1px;
    height: 20px;
    @include themed() {
      background-color: t($grey-15);
    }
  }

  // "Add step to sequence" row below all cards
  .step-add-row {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0;
  }
}
</style>
